/**
 * Keeps `type: "addons"` from carrying nested search-filter lists.
 * Strips all addons entries, hoists filter-like objects found inside `addons.value`
 * (including double-nested arrays), and builds one addons object whose `value` is
 * only string slugs (merged with authoritative slugs from the booking flow).
 */
export type BookingPayloadFilter = {
  type?: string;
  [key: string]: unknown;
};
export function normalizeSearchFiltersForBooking(
  filters: unknown[] | null | undefined,
  authoritativeAddonSlugs: string[],
): {
  filters: Record<string, unknown>[];
  addonsEntry: { type: "addons"; value: string[] } | null;
} {
  const out: Record<string, unknown>[] = [];
  const seen = new Set<string>();
  const slugParts: string[] = [];

  const isRecord = (x: unknown): x is Record<string, unknown> =>
    x !== null && typeof x === "object" && !Array.isArray(x);

  const filterDedupKey = (f: Record<string, unknown>): string => {
    if (f.type === "addon" && f.value != null) {
      return `addon:${String(f.value)}`;
    }
    const id = f.search_field_id ?? f.field_name ?? "";
    return `${String(f.type)}:${String(id)}`;
  };

  const addFilter = (f: Record<string, unknown>) => {
    if (typeof f.type !== "string") return;
    const k = filterDedupKey(f);
    if (seen.has(k)) return;
    seen.add(k);
    out.push(f);
  };

  const collectFromAddonValue = (val: unknown): void => {
    if (typeof val === "string" && val.trim()) {
      slugParts.push(val.trim());
      return;
    }
    if (!Array.isArray(val)) return;
    for (const item of val) {
      if (typeof item === "string" && item.trim()) {
        slugParts.push(item.trim());
      } else if (Array.isArray(item)) {
        for (const inner of item) {
          if (typeof inner === "string" && inner.trim()) {
            slugParts.push(inner.trim());
          } else if (isRecord(inner) && typeof inner.type === "string") {
            addFilter(inner);
          }
        }
      } else if (isRecord(item) && typeof item.type === "string") {
        addFilter(item);
      }
    }
  };

  for (const f of filters || []) {
    if (!isRecord(f)) continue;
    if (f.type === "addons") {
      collectFromAddonValue(f.value);
      continue;
    }
    addFilter(f);
  }

  for (const s of authoritativeAddonSlugs) {
    if (typeof s === "string" && s.trim()) slugParts.push(s.trim());
  }

  const uniqueSlugs = [...new Set(slugParts)];
  const addonsEntry =
    uniqueSlugs.length > 0
      ? { type: "addons" as const, value: uniqueSlugs }
      : null;

  return { filters: out, addonsEntry };
}

export function stripBookingFieldIds(
  filters: unknown[] | null | undefined,
): BookingPayloadFilter[] {
  const sanitizedFilters: BookingPayloadFilter[] = [];

  for (const filter of filters || []) {
    if (
      filter === null ||
      typeof filter !== "object" ||
      Array.isArray(filter)
    ) {
      continue;
    }

    const sanitizedFilter = { ...filter } as BookingPayloadFilter;
    delete sanitizedFilter.booking_field_id;
    sanitizedFilters.push(sanitizedFilter);
  }

  return sanitizedFilters;
}

/** Final `searchFilters` array for booking contract payloads (incl. missing filters). */
export function buildSearchFiltersForContractPayload(
  searchFilters: unknown[] | null | undefined,
  selectedAddonSlugs: string[],
  missingFiltersFlat: unknown[],
): BookingPayloadFilter[] {
  const { filters, addonsEntry } = normalizeSearchFiltersForBooking(
    searchFilters,
    selectedAddonSlugs,
  );

  const withAddons = addonsEntry ? [...filters, addonsEntry] : [...filters];

  return stripBookingFieldIds([...withAddons, ...missingFiltersFlat]);
}
